import React, { useState, useCallback, useRef } from 'react';
import moment from 'moment';
import { palette, Modal, Icon } from '@m12s/component-library';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { getLatestOperatorRunEndAt } from 'lib/selectors/getLatestOperatorRunEndAt';
import { getLatestOperator } from 'lib/selectors/getLatestOperator';
import { getShiftName } from 'lib/selectors/getShiftName';
import { getShiftStart } from 'lib/selectors/getShiftStart';
import { getAllowStartOfShift } from 'lib/selectors/getAllowStartOfShift';
import { getAllowPreviousSignOut } from 'lib/selectors/getAllowPreviousSignOut';
import { getOperatorIsLoggingIn } from 'lib/selectors/getOperatorIsLoggingIn';
import { getEnableOperatorVerification } from 'lib/selectors/getEnableOperatorVerification';

import { timesCircle } from 'lib/icons';

import { P } from 'components/Text';
import PinVerificationModal from 'components/PinVerificationModal';
import { Button, Title, IconWrapper } from './styled';

const SignInModal = ({ operator, onClose, onSubmit, isOpen }) => {
  const { t } = useTranslation();

  const shiftName = useSelector(getShiftName);
  const shiftStart = useSelector(getShiftStart);
  const latestOperatorRunEndAt = useSelector(getLatestOperatorRunEndAt);
  const latestOperator = useSelector(getLatestOperator);
  const allowPreviousSignOut = useSelector(getAllowPreviousSignOut);
  const allowStartOfShift = useSelector(getAllowStartOfShift);
  const operatorIsLoggingIn = useSelector(getOperatorIsLoggingIn);
  const enableOperatorVerification = useSelector(getEnableOperatorVerification);

  // PIN verification state
  const [showPinVerification, setShowPinVerification] = useState(false);
  const pendingSubmitOptsRef = useRef(null);

  // Handle submit with optional PIN verification
  const handleSubmitWithVerification = useCallback(
    (submitOpts) => {
      if (enableOperatorVerification && operator?.id) {
        // Store the pending submission and show PIN modal
        pendingSubmitOptsRef.current = submitOpts;
        setShowPinVerification(true);
      } else {
        // No verification required, submit directly
        onSubmit(operator, submitOpts);
      }
    },
    [enableOperatorVerification, operator, onSubmit]
  );

  // Handle PIN verification success
  const handlePinSuccess = useCallback(async () => {
    const submitOpts = pendingSubmitOptsRef.current;
    pendingSubmitOptsRef.current = null;

    if (submitOpts) {
      await onSubmit(operator, submitOpts);
    }

    setShowPinVerification(false);
  }, [operator, onSubmit]);

  // Handle PIN verification cancel
  const handlePinCancel = useCallback(() => {
    pendingSubmitOptsRef.current = null;
    setShowPinVerification(false);
  }, []);

  // Disable buttons when PIN verification modal is open
  const buttonsDisabled = operatorIsLoggingIn || showPinVerification;

  return (
    <>
      <Modal
        isOpen={isOpen || !!operator}
        onClose={onClose}
        header={
          <Title color={palette.White}>
            {t('What time would you like to login at?')}
          </Title>
        }
        headerIcon={
          <IconWrapper onClick={onClose}>
            <Icon size="2rem" color={palette.Purple200} icon={timesCircle} />
          </IconWrapper>
        }
        headerStyles={{
          background: `linear-gradient(180deg, ${palette.Purple500} 0%, #9272DB 100%)`,
        }}
        details={
          <>
            {allowPreviousSignOut && (
              <P>
                <strong>{t('Last Sign Out')}:</strong> &quot;
                {latestOperator?.name || 'Previous Operator'}&quot;{' '}
                {t('signed out at')}{' '}
                {moment(latestOperatorRunEndAt).format('LT')}
              </P>
            )}
            {allowStartOfShift && (
              <P>
                <strong>{t('Current Shift')}:</strong> &quot;{shiftName}&quot;{' '}
                {t('started at')} {moment(shiftStart).format('LT')}
              </P>
            )}
          </>
        }
        buttons={
          <>
            {allowPreviousSignOut && (
              <Button
                variant="tertiary"
                disabled={buttonsDisabled}
                onClick={() => {
                  return handleSubmitWithVerification({
                    pinToPreviousEvent: true,
                  });
                }}
              >
                {t('Last Sign Out')}
              </Button>
            )}
            {allowStartOfShift && (
              <Button
                variant="tertiary"
                disabled={buttonsDisabled}
                onClick={() => {
                  return handleSubmitWithVerification({
                    pinToPreviousEvent: true,
                    startAt: shiftStart,
                  });
                }}
              >
                {t('Start Of Shift')} - {moment(shiftStart).format('LT')}
              </Button>
            )}
            <Button
              disabled={buttonsDisabled}
              onClick={() => {
                return handleSubmitWithVerification({
                  pinToPreviousEvent: false,
                });
              }}
            >
              {t('Now')}
            </Button>
          </>
        }
      />

      {enableOperatorVerification && operator?.id && (
        <PinVerificationModal
          isOpen={showPinVerification}
          operatorId={operator.id}
          onSuccess={handlePinSuccess}
          onCancel={handlePinCancel}
        />
      )}
    </>
  );
};

SignInModal.propTypes = {
  operator: PropTypes.object,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isOpen: PropTypes.bool,
};

export { SignInModal };
